<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 class="text-3xl font-bold text-slate-100">Caixas de e-mail</h1>
          <p class="text-slate-400 mt-2 text-sm">
            IMAP ou Microsoft 365 (Graph) para o agente
            <strong class="text-slate-300">Analista de e-mail</strong>.
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 px-4 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 text-sm font-semibold transition"
          @click="openModal(null)"
        >
          Nova caixa de email
        </button>
      </div>

      <div v-if="loadError" class="mb-4 p-4 bg-red-500/10 border border-red-400/40 text-red-300 rounded-lg text-sm">
        {{ loadError }}
      </div>

      <div v-if="loading" class="text-center py-12 text-slate-400">Carregando…</div>

      <ul v-else-if="connectors.length" class="space-y-3">
        <li
          v-for="c in connectors"
          :key="c.id"
          class="rounded-xl border border-cyan-400/25 bg-slate-900/80 backdrop-blur-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <h2 class="text-lg font-semibold text-slate-100">{{ c.name }}</h2>
              <span
                class="text-xs px-2 py-0.5 rounded-full border"
                :class="c.active ? 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10' : 'border-slate-600 text-slate-400'"
              >
                {{ c.active ? 'Ativa' : 'Inativa' }}
              </span>
            </div>
            <p class="text-sm text-slate-400 mt-1 font-mono break-all">
              <span class="text-cyan-300/90">{{ c.display_protocol_label || 'IMAP' }}</span>
              <template v-if="c.connector_type === 'api'">
                · {{ c.graph_mailbox || '—' }}
              </template>
              <template v-else>
                · {{ c.imap_user_name }} @ {{ c.imap_host }}:{{ c.imap_port }} / {{ c.imap_mailbox }}
              </template>
            </p>
          </div>
          <div class="flex gap-2 shrink-0">
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-cyan-400/45 text-cyan-200 hover:bg-cyan-400/12"
              @click="openModal(c.id)"
            >
              Editar
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-red-400/45 text-red-200 hover:bg-red-500/15"
              @click="confirmDelete(c)"
            >
              Excluir
            </button>
          </div>
        </li>
      </ul>

      <p v-else class="text-center text-slate-500 py-12 text-sm">Nenhuma caixa cadastrada.</p>
    </div>

    <div
      v-if="modalOpen"
      class="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-sm overflow-y-auto overflow-x-hidden"
    >
      <div
        class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-lg w-full min-w-0 max-h-[90vh] flex flex-col overflow-hidden my-8"
      >
        <div class="px-6 py-4 bg-[#2f4563] border-b border-cyan-200/25 flex justify-between items-start gap-3 flex-shrink-0">
          <h3 class="text-lg font-semibold text-slate-100">
            {{ editingId ? 'Editar caixa de email' : 'Nova caixa de email' }}
          </h3>
          <button
            type="button"
            class="text-slate-500 hover:text-cyan-300 p-1 -mr-1 transition"
            aria-label="Fechar"
            @click="closeModal"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form class="flex flex-col flex-1 min-h-0" @submit.prevent="openSaveConfirm">
          <div
            class="px-6 py-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0 [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
          >
            <div class="space-y-4">

              <div>
            <div
              class="flex flex-wrap gap-2"
              role="radiogroup"
              aria-labelledby="inbox-connector-kind-label"
            >
              <label
                class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition shrink-0"
                :class="
                  form.connector_type === 'imap'
                    ? 'border-cyan-400/70 bg-cyan-400/10 text-cyan-100'
                    : 'border-slate-500/80 bg-slate-800/40 text-slate-300'
                "
              >
                <input v-model="form.connector_type" type="radio" value="imap" class="text-cyan-500" />
                <span>IMAP</span>
              </label>
              <label
                class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition shrink-0"
                :class="
                  form.connector_type === 'api'
                    ? 'border-cyan-400/70 bg-cyan-400/10 text-cyan-100'
                    : 'border-slate-500/80 bg-slate-800/40 text-slate-300'
                "
              >
                <input v-model="form.connector_type" type="radio" value="api" class="text-cyan-500" />
                <span>API</span>
              </label>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Nome <span class="text-red-400">*</span></label>
            <input
              v-model="form.name"
              type="text"
              required
              class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
            />
          </div>

          <template v-if="form.connector_type === 'api'">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Provedor API <span class="text-red-400">*</span></label>
              <select
                v-model="form.api_provider"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              >
                <option value="microsoft_graph">Microsoft 365 (Graph)</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">ID do diretório (tenant) <span class="text-red-400">*</span></label>
              <input
                v-model="form.graph_tenant_id"
                type="text"
                required
                autocomplete="off"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">ID do Cliente (Aplicativo) <span class="text-red-400">*</span></label>
              <input
                v-model="form.graph_client_id"
                type="text"
                required
                autocomplete="off"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Chave secreta <span class="text-red-400">*</span></label>
              <input
                v-model="form.graph_client_secret"
                type="password"
                :required="!editingId || !form.graph_client_secret_configured"
                autocomplete="new-password"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                :placeholder="editingId && form.graph_client_secret_configured ? 'Deixe em branco para manter' : ''"
              />
              <label class="mt-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                <input v-model="form.clear_graph_client_secret" type="checkbox" class="rounded border-slate-500" />
                Remover chave secreta salva
              </label>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Caixa de entrada (UPN) <span class="text-red-400">*</span></label>
              <input
                v-model="form.graph_mailbox"
                type="text"
                required
                autocomplete="off"
                placeholder="utilizador@empresa.onmicrosoft.com"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              />
            </div>
          </template>

          <div v-if="form.connector_type === 'imap'" class="space-y-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-slate-300 mb-1">Servidor IMAP <span class="text-red-400">*</span></label>
                <input
                  v-model="form.imap_host"
                  type="text"
                  required
                  placeholder="imap.gmail.com"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Porta <span class="text-red-400">*</span></label>
                <input
                  v-model.number="form.imap_port"
                  type="number"
                  min="1"
                  max="65535"
                  required
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                />
              </div>
              <div class="flex items-end pb-1">
                <label class="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input v-model="form.imap_ssl" type="checkbox" class="rounded border-slate-500" />
                  SSL/TLS (recomendado)
                </label>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Usuário (e-mail) <span class="text-red-400">*</span></label>
              <input
                v-model="form.imap_user_name"
                type="text"
                required
                autocomplete="username"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Senha IMAP <span class="text-red-400">*</span></label>
              <div class="relative">
                <input
                  v-model="form.imap_password"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  class="w-full pl-3 pr-11 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                  :placeholder="editingId && form.imap_password_configured ? 'Deixe em branco para manter' : ''"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-[#2AD7DE] rounded-r-lg"
                  :aria-label="showPassword ? 'Ocultar senha' : 'Mostrar senha'"
                  @click="showPassword = !showPassword"
                >
                  <svg v-if="!showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                </button>
              </div>
              <label class="mt-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                <input v-model="form.clear_imap_password" type="checkbox" class="rounded border-slate-500" />
                Remover senha salva
              </label>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Pasta (mailbox)</label>
              <input
                v-model="form.imap_mailbox"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                placeholder="INBOX"
              />
            </div>
          </div>

          <label class="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input v-model="form.active" type="checkbox" class="rounded border-slate-500" />
            ativo
          </label>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-slate-600/70 flex flex-col gap-3 flex-shrink-0 bg-slate-700/55">
            <div
              v-if="formError"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissEmailInboxFormError"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Não foi possível salvar.</span>
              <span class="text-red-100/95 break-words pr-1">{{ formError }}</span>
            </div>
            <div
              v-if="testError"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissTestMessages"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Erro no teste.</span>
              <span class="text-red-100/95 break-words pr-1">{{ testError }}</span>
            </div>
            <div
              v-if="testOkMessage"
              role="status"
              class="relative w-full rounded-lg border border-emerald-400/45 bg-emerald-500/10 pl-3 pr-10 py-2.5 text-sm text-emerald-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-emerald-200/90 hover:text-emerald-50 hover:bg-emerald-500/20 transition leading-none"
                aria-label="Fechar"
                @click="dismissTestMessages"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="text-emerald-100/95 break-words pr-1">{{ testOkMessage }}</span>
            </div>
            <div class="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-2">
              <button
                type="button"
                :disabled="testLoading || saving"
                class="px-4 py-2 text-sm rounded-lg border border-cyan-400/50 text-cyan-100 hover:bg-cyan-400/10 transition disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                @click="testInboxConnection"
              >
                {{ testLoading ? 'Testando…' : 'Testar conexão' }}
              </button>
              <div class="flex justify-end gap-2 w-full sm:w-auto">
                <button
                  type="submit"
                  :disabled="saving"
                  class="px-4 py-2 text-sm rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {{ saving ? 'Salvando…' : 'Salvar' }}
                </button>
                <button
                  type="button"
                  class="px-4 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800/70"
                  @click="closeModal"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="saveConfirmOpen"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      @click.self="closeSaveConfirm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">Deseja salvar esta caixa de e-mail?</p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="saving"
            class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
            @click="confirmSave"
          >
            {{ saving ? 'Salvando…' : 'Sim' }}
          </button>
          <button
            type="button"
            :disabled="saving"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition disabled:opacity-50"
            @click="closeSaveConfirm"
          >
            Não
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="deleteTarget"
      class="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl max-w-md w-full p-6 shadow-2xl">
        <h3 class="text-lg font-semibold text-slate-100 mb-2">Excluir caixa?</h3>
        <p class="text-slate-300 mb-4 text-sm">{{ deleteTarget.name }}</p>
        <div class="flex justify-end gap-2">
          <button type="button" class="px-4 py-2 rounded-lg border border-slate-500 text-slate-200" @click="deleteTarget = null">
            Cancelar
          </button>
          <button
            type="button"
            :disabled="deleting"
            class="px-4 py-2 rounded-lg bg-red-500/20 border border-red-400/40 text-red-200 disabled:opacity-50"
            @click="executeDelete"
          >
            {{ deleting ? 'Excluindo…' : 'Excluir' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi.js'

export default {
  name: 'EmailInboxConnectors',

  data() {
    return {
      loading: false,
      loadError: '',
      connectors: [],
      modalOpen: false,
      editingId: null,
      saving: false,
      formError: '',
      showPassword: false,
      deleteTarget: null,
      deleting: false,
      saveConfirmOpen: false,
      testLoading: false,
      testError: '',
      testOkMessage: '',
      form: this.emptyForm()
    }
  },

  mounted() {
    this.load()
  },

  methods: {
    dismissEmailInboxFormError() {
      this.formError = ''
    },

    dismissTestMessages() {
      this.testError = ''
      this.testOkMessage = ''
    },

    async testInboxConnection() {
      this.dismissTestMessages()
      const { post } = useApi()

      if (this.form.connector_type === 'api') {
        if (this.form.clear_graph_client_secret && !this.form.graph_client_secret?.trim()) {
          this.testError =
            'Não é possível testar com «Remover client secret salvo» sem informar um novo secret. Desmarque ou preencha o campo.'
          return
        }
        if (!this.form.graph_client_secret?.trim()) {
          if (!this.editingId || !this.form.graph_client_secret_configured) {
            this.testError = 'Informe a chave secreta para testar.'
            return
          }
        }
        if (
          !this.form.graph_tenant_id?.trim() ||
          !this.form.graph_client_id?.trim() ||
          !this.form.graph_mailbox?.trim()
        ) {
          this.testError = 'Preencha ID do diretório (tenant), ID do Cliente (Aplicativo) e caixa de entrada (UPN).'
          return
        }

        this.testLoading = true
        const body = {
          id: this.editingId || undefined,
          connector_type: 'api',
          api_provider: this.form.api_provider || 'microsoft_graph',
          graph_tenant_id: this.form.graph_tenant_id.trim(),
          graph_client_id: this.form.graph_client_id.trim(),
          graph_mailbox: this.form.graph_mailbox.trim(),
          clear_graph_client_secret: this.form.clear_graph_client_secret,
          graph_client_secret: this.form.clear_graph_client_secret ? null : this.form.graph_client_secret || null
        }
        const res = await post('/api/v1/email_inbox_connectors/test_connection', { email_inbox_connector: body })
        this.testLoading = false
        if (res.error) {
          const d = res.error.response?.data
          this.testError =
            d?.error || (Array.isArray(d?.errors) ? d.errors.join(', ') : null) || 'Erro ao testar Microsoft Graph.'
          return
        }
        const d = res.data
        if (d?.ok) {
          this.testOkMessage = d.message || 'Conexão validada.'
        } else {
          this.testError = d?.error || 'Falha no teste Graph.'
        }
        return
      }

      if (this.form.clear_imap_password && !this.form.imap_password?.trim()) {
        this.testError =
          'Não é possível testar com «Remover senha salva» marcado sem informar uma nova senha. Desmarque ou preencha a senha.'
        return
      }
      if (!this.form.imap_password?.trim()) {
        if (!this.editingId || !this.form.imap_password_configured) {
          this.testError = 'Informe a senha IMAP para testar.'
          return
        }
      }
      if (!this.form.imap_host?.trim() || !this.form.imap_user_name?.trim()) {
        this.testError = 'Preencha servidor IMAP e usuário (e-mail) para testar.'
        return
      }

      this.testLoading = true
      const body = {
        id: this.editingId || undefined,
        connector_type: 'imap',
        imap_host: this.form.imap_host.trim(),
        imap_port: this.form.imap_port,
        imap_user_name: this.form.imap_user_name.trim(),
        imap_mailbox: (this.form.imap_mailbox || 'INBOX').trim(),
        imap_ssl: this.form.imap_ssl,
        clear_imap_password: this.form.clear_imap_password,
        imap_password: this.form.clear_imap_password ? null : this.form.imap_password || null
      }
      const res = await post('/api/v1/email_inbox_connectors/test_connection', { email_inbox_connector: body })
      this.testLoading = false
      if (res.error) {
        const d = res.error.response?.data
        this.testError =
          d?.error || (Array.isArray(d?.errors) ? d.errors.join(', ') : null) || 'Erro ao testar conexão IMAP.'
        return
      }
      const d = res.data
      if (d?.ok) {
        this.testOkMessage = d.message || 'Conexão validada.'
      } else {
        this.testError = d?.error || 'Falha no teste IMAP.'
      }
    },

    openSaveConfirm() {
      if (this.saving) return
      this.saveConfirmOpen = true
    },

    closeSaveConfirm() {
      if (this.saving) return
      this.saveConfirmOpen = false
    },

    async confirmSave() {
      if (this.saving) return
      await this.saveModal()
    },

    emptyForm() {
      return {
        name: '',
        connector_type: 'imap',
        api_provider: 'microsoft_graph',
        graph_tenant_id: '',
        graph_client_id: '',
        graph_client_secret: '',
        clear_graph_client_secret: false,
        graph_client_secret_configured: false,
        graph_mailbox: '',
        imap_host: '',
        imap_port: 993,
        imap_user_name: '',
        imap_password: '',
        clear_imap_password: false,
        imap_password_configured: false,
        imap_mailbox: 'INBOX',
        imap_ssl: true,
        active: true
      }
    },

    async load() {
      const { get } = useApi()
      this.loading = true
      this.loadError = ''
      const res = await get('/api/v1/email_inbox_connectors')
      this.loading = false
      if (res.error) {
        this.loadError = res.error.response?.data?.errors?.join?.(', ') || 'Erro ao carregar.'
        this.connectors = []
        return
      }
      this.connectors = res.data?.email_inbox_connectors || []
    },

    async openModal(id) {
      this.formError = ''
      this.dismissTestMessages()
      this.showPassword = false
      this.editingId = id
      if (id) {
        const { get } = useApi()
        const res = await get(`/api/v1/email_inbox_connectors/${id}`)
        if (res.error || !res.data?.email_inbox_connector) {
          this.formError = 'Não foi possível carregar a caixa.'
          return
        }
        const c = res.data.email_inbox_connector
        this.form = {
          name: c.name || '',
          connector_type: c.connector_type === 'api' ? 'api' : 'imap',
          api_provider: c.api_provider || 'microsoft_graph',
          graph_tenant_id: c.graph_tenant_id || '',
          graph_client_id: c.graph_client_id || '',
          graph_client_secret: '',
          clear_graph_client_secret: false,
          graph_client_secret_configured: !!c.graph_client_secret_configured,
          graph_mailbox: c.graph_mailbox || '',
          imap_host: c.imap_host || '',
          imap_port: c.imap_port ?? 993,
          imap_user_name: c.imap_user_name || '',
          imap_password: '',
          clear_imap_password: false,
          imap_password_configured: !!c.imap_password_configured,
          imap_mailbox: c.imap_mailbox || 'INBOX',
          imap_ssl: c.imap_ssl !== false,
          active: c.active !== false
        }
      } else {
        this.form = this.emptyForm()
      }
      this.modalOpen = true
    },

    closeModal() {
      this.modalOpen = false
      this.editingId = null
      this.saveConfirmOpen = false
      this.formError = ''
      this.dismissTestMessages()
      this.form = this.emptyForm()
    },

    async saveModal() {
      this.saving = true
      this.formError = ''
      const { post, put } = useApi()
      let body
      if (this.form.connector_type === 'api') {
        body = {
          name: this.form.name.trim(),
          connector_type: 'api',
          api_provider: this.form.api_provider || 'microsoft_graph',
          graph_tenant_id: this.form.graph_tenant_id.trim(),
          graph_client_id: this.form.graph_client_id.trim(),
          graph_mailbox: this.form.graph_mailbox.trim(),
          active: this.form.active,
          clear_graph_client_secret: this.form.clear_graph_client_secret,
          graph_client_secret: this.form.clear_graph_client_secret ? null : this.form.graph_client_secret
        }
      } else {
        body = {
          name: this.form.name.trim(),
          connector_type: 'imap',
          imap_host: this.form.imap_host.trim(),
          imap_port: this.form.imap_port,
          imap_user_name: this.form.imap_user_name.trim(),
          imap_mailbox: (this.form.imap_mailbox || 'INBOX').trim(),
          imap_ssl: this.form.imap_ssl,
          active: this.form.active,
          clear_imap_password: this.form.clear_imap_password,
          imap_password: this.form.clear_imap_password ? null : this.form.imap_password
        }
      }
      let res
      if (this.editingId) {
        res = await put(`/api/v1/email_inbox_connectors/${this.editingId}`, { email_inbox_connector: body })
      } else {
        res = await post('/api/v1/email_inbox_connectors', { email_inbox_connector: body })
      }
      this.saving = false
      if (res.error) {
        const msg = res.error.response?.data?.errors
        this.formError = Array.isArray(msg) ? msg.join(', ') : 'Erro ao salvar.'
        return
      }
      this.saveConfirmOpen = false
      this.closeModal()
      await this.load()
    },

    confirmDelete(c) {
      this.deleteTarget = c
    },

    async executeDelete() {
      if (!this.deleteTarget) return
      const id = this.deleteTarget.id
      this.deleting = true
      const { delete: del } = useApi()
      const res = await del(`/api/v1/email_inbox_connectors/${id}`)
      this.deleting = false
      this.deleteTarget = null
      if (!res.error) {
        this.connectors = this.connectors.filter((c) => String(c.id) !== String(id))
      }
    }
  }
}
</script>
